import { Text, View, ScrollView, RefreshControl, Pressable, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { LegendList } from "@legendapp/list";
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { setPermissionLocked } from '@/data/roles';
import { showAlert } from '@/utils/showAlert';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import usePlatform from '@/utils/hooks/usePlatform';
import PageTitle from '@/components/General/PageTitle';
import ContentLoader from '@/components/General/ContentLoader';
import { useRouter, useNavigation, useLocalSearchParams } from 'expo-router';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import Avatar from '@/components/People/Avatar';
import MaterialIcon from "@react-native-vector-icons/material-icons";
import Unicon from '@/components/General/Unicon';
import Animated, { FadeInDown } from 'react-native-reanimated';
import Callout from '@/components/General/Callout';
import React from 'react';
import {Tooltip} from '@/components/General/Tooltip';

// ============ INFO PANEL ============
import { getPermissionDisplayLabel } from '@/utils/permissionLabels';

const PermissionInfoPanel = React.memo(({ permissionID }) => {
    const queryClient = useQueryClient();
    const { data: permission, isFetching } = Query.permissions.byId({ id: permissionID });
    const { data: loggedInUser } = Query.people.loggedInUser();
    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.permissions.byId(permissionID) });

    const isAccountOwner = loggedInUser?.isAccountOwner || false;

    // Account Owner only: lock/unlock this permission for the org (OrganizationLockedPermissions row)
    const toggleLockMutation = useMutation({
        mutationFn: setPermissionLocked,
        onSuccess: (success) => {
            if (!success) return;
            queryClient.invalidateQueries({ queryKey: QueryKeys.permissions.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.permissions.byId(permissionID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.roleFolders.all() });
        }
    });

    // Locking is the safe direction (no confirmation). Unlocking needs a confirm —
    // roles with the permission become manageable by others.
    const handleToggleLock = () => {
        if (permission?.locked) {
            showAlert({
                title: `Unlock ${getPermissionDisplayLabel(permission?.name)}?`,
                subtitle: "Roles with this permission will now be able to be managed by others.",
                confirmText: "Unlock",
                isDestructive: false,
                onConfirm: () => toggleLockMutation.mutate({ permissionID, locked: false })
            });
        } else {
            toggleLockMutation.mutate({ permissionID, locked: true });
        }
    };

    const roleCount = permission?.Roles?.length || 0;
    const userCount = permission?.affectedUsers?.length || 0;

    return (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh} />}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
            >
                {/* Header Card */}
                <Animated.View entering={FadeInDown.duration(400)} style={styles.headerCard}>
                    {/* Locked Badge + Account Owner lock toggle */}
                    <View style={styles.lockedRow}>
                        {permission?.locked && (
                            <Tooltip style={{flex:1, height:100}} title="This permission is locked and any Roles containing it cannot be modified.">
                                <View style={styles.lockedBadge}>
                                    <Unicon name="lock-closed" size={14} colorVariant="warning" />
                                    <Text style={styles.lockedBadgeText}>Locked Permission</Text>
                                </View>
                            </Tooltip>
                        )}
                        {isAccountOwner && (
                            <Tooltip
                                title={permission?.locked ? "Unlock permission" : "Lock permission"}
                                subtitle={permission?.locked ? "Roles with this permission will be able to be managed by others." : "Roles with this permission cannot be modified while locked."}
                                onPress={handleToggleLock}
                                style={styles.lockToggleButton(permission?.locked)}
                                testID="PermissionInfo-LockToggle"
                            >
                                <Unicon name={permission?.locked ? "lock-closed" : "lock-open-outline"} size={18} colorVariant={permission?.locked ? "warning" : "textSecondary"} />
                            </Tooltip>
                        )}
                    </View>

                    {/* Title Row */}
                    <View style={styles.titleRow}>
                        <View style={styles.iconBadge}>
                            <Unicon name="key" size={24} colorVariant="secondary" />
                        </View>
                        <View style={styles.titleInfo}>
                            <Text style={styles.permissionTitle}>{getPermissionDisplayLabel(permission?.name)}</Text>
                            {!!permission?.description && <Text style={styles.permissionSubtitle}>{permission.description}</Text>}
                        </View>
                    </View>

                    {/* Stats Row */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Roles</Text>
                            <Text style={styles.statValue}>{roleCount}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Users</Text>
                            <Text style={styles.statValue}>{userCount}</Text>
                        </View>
                    </View>
                </Animated.View>

                {/* No Roles Callout */}
                {roleCount === 0 && !isFetching && (
                    <Animated.View entering={FadeInDown.delay(150).duration(400)}>
                        <Callout type="warning" message="This permission has no roles assigned. Only admins will be able to access this feature." />
                    </Animated.View>
                )}
            </ScrollView>
        </View>
    );
});

// ============ ROLES TAB ============
const RolesTab = React.memo(({ permissionID }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: permission, isFetching } = Query.permissions.byId({ id: permissionID });
    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.permissions.byId(permissionID) });

    return (
        <View style={styles.tabContent}>
            <LegendList
                recycleItems
                keyExtractor={(item) => item.id}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                estimatedItemSize={70}
                data={isFetching ? [] : permission?.Roles}
                renderItem={({ item }) => (
                    <Pressable
                        style={styles.roleItem}
                        onPress={() => router.push({ pathname: "/roles/" + item?.id })}
                    >
                        <View style={styles.roleItemIcon}>
                            <Unicon name="shield-checkmark" size={18} colorVariant="primary" />
                        </View>
                        <View style={styles.roleItemInfo}>
                            <Text style={styles.roleItemName} numberOfLines={1}>{item?.name}</Text>
                            {!!item?.description && <Text style={styles.roleItemDesc} numberOfLines={2}>{item.description}</Text>}
                        </View>
                        <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                    </Pressable>
                )}
                contentContainerStyle={styles.listContent}
                refreshControl={<RefreshControl refreshing={false} onRefresh={refresh} />}
                ListFooterComponent={isFetching
                    ? <ContentLoader containerHeight={70} spacingVertical={8} amount={6} />
                    : permission?.Roles?.length === 0
                        ? <EmptyListPlaceholder icon="people" spacing={20} message="This permission currently doesn't have any roles. Only admins will be able to access this feature!" />
                        : <View style={{ paddingBottom: 80 }} />
                }
            />
        </View>
    );
});

// ============ USER CARD ============
const UserCard = React.memo(({ user, router }) => {
    const isRegistered = user?.RegisteredUsersOrganizations?.length > 0;
    const isAdmin = user?.RegisteredUsersOrganizations?.[0]?.isAdmin;

    return (
        <Pressable
            style={styles.userCard}
            onPress={() => router.push(`/people/${user.id}`)}
        >
            <View style={styles.userCardHeader}>
                <Avatar size={44} profile={user} disableBorder />
                <View style={styles.userCardInfo}>
                    <View style={styles.userNameRow}>
                        <Text style={styles.userName}>{user.firstName} {user.lastName}</Text>
                        {isRegistered && (
                            <MaterialIcon name="verified" size={16} color={styles.verifiedIcon.color} />
                        )}
                        {isAdmin && (
                            <MaterialIcon name="admin-panel-settings" size={16} color={styles.adminIcon.color} />
                        )}
                    </View>
                    <Text style={styles.userStatus}>
                        {isAdmin ? 'Admin' : isRegistered ? 'Reach User' : 'Not Registered'}
                    </Text>
                </View>
                <Unicon name="chevron-forward" size={18} colorVariant="textSecondary" />
            </View>
            <View style={styles.rolesSection}>
                <Text style={styles.rolesSectionLabel}>Granted through:</Text>
                <View style={styles.rolesRow}>
                    {user.Roles.map(r => (
                        <View key={r} style={styles.rolePill}>
                            <Unicon name="shield-checkmark" size={12} colorVariant="primary" />
                            <Text style={styles.rolePillText}>{r}</Text>
                        </View>
                    ))}
                </View>
            </View>
        </Pressable>
    );
});

// ============ USERS TAB ============
const UsersTab = React.memo(({ permissionID }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: permission, isFetching } = Query.permissions.byId({ id: permissionID });
    const refresh = () => queryClient.invalidateQueries({ queryKey: QueryKeys.permissions.byId(permissionID) });

    return (
        <View style={styles.tabContent}>
            <LegendList
                keyExtractor={(item) => item.id}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                estimatedItemSize={120}
                data={isFetching ? [] : permission?.affectedUsers}
                renderItem={({ item }) => <UserCard user={item} router={router} />}
                contentContainerStyle={styles.listContent}
                refreshControl={<RefreshControl refreshing={false} onRefresh={refresh} />}
                ListFooterComponent={isFetching
                    ? <ContentLoader containerHeight={120} spacingVertical={8} amount={4} />
                    : permission?.affectedUsers?.length === 0
                        ? <EmptyListPlaceholder icon="person" spacing={20} message="No users currently have this permission. Only admins will be able to access this feature!" />
                        : <View style={{ paddingBottom: 80 }} />
                }
/>
        </View>
    );
});

// ============ MAIN SCREEN ============
const PermissionScreen = () => {
    const [mobileTab, setMobileTab] = useState(0);
    const [activityTab, setActivityTab] = useState(0);

    const { isNative } = usePlatform();
    const navigation = useNavigation();
    const router = useRouter();
    const { permissionID } = useLocalSearchParams();
    const { width } = useWindowDimensions();
    const isWideScreen = width >= 900;

    if (!permissionID) {
        router.canGoBack() ? router.back() : router.replace("/");
    }

    const { data: permission, isFetching: fetchingPermission } = Query.permissions.byId({ id: permissionID });

    useEffect(() => {
        navigation.setOptions({
            headerTitle: () => <PageTitle title={getPermissionDisplayLabel(permission?.name)} subtitle="Permission" />,
        });
    }, [navigation, permission]);

    const mobileRoutes = useMemo(() => [
        { key: 'info', title: 'Info' },
        { key: 'roles', title: 'Roles' },
        { key: 'users', title: 'Affected Users' },
    ], []);

    const activityRoutes = useMemo(() => [
        { key: 'roles', title: 'Roles' },
        { key: 'users', title: 'Affected Users' },
    ], []);

    const renderTabBar = useCallback((props) => <ReachTabBar {...props} />, []);

    const renderActivityScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'roles': return <RolesTab permissionID={permissionID} />;
            case 'users': return <UsersTab permissionID={permissionID} />;
            default: return null;
        }
    }, [permissionID]);

    const renderMobileScene = useCallback(({ route }) => {
        if (route.key === 'info') return <PermissionInfoPanel permissionID={permissionID} />;
        return renderActivityScene({ route });
    }, [permissionID, renderActivityScene]);

    // ============ LOADING ============
    if (!permission?.id && fetchingPermission) {
        return (
            <Animated.View entering={FadeInDown.duration(300)} style={styles.loadingContainer}>
                <View style={styles.loadingHeaderRow}>
                    <ContentLoader containerHeight={52} containerWidth={52} borderRadius={10} spacingVertical={0} />
                    <View style={styles.loadingHeaderInfo}>
                        <ContentLoader containerHeight={22} containerWidth="70%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                        <ContentLoader containerHeight={14} containerWidth="40%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                    </View>
                </View>
                <View style={styles.loadingStatsRow}>
                    <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                    <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                </View>
                <ContentLoader containerHeight={180} borderRadius={12} staggerPosition={4} />
            </Animated.View>
        );
    }

    if (!permission?.id && !fetchingPermission) return <EmptyListPlaceholder type="404" message="Permission not found" />;

    // ============ WIDE SCREEN ============
    if (isWideScreen) {
        return (
            <View style={styles.container}>
                <View style={styles.twoColumn}>
                    <PermissionInfoPanel permissionID={permissionID} />
                    <View style={styles.rightPanel}>
                        <TabView
                            navigationState={{ index: activityTab, routes: activityRoutes }}
                            renderScene={renderActivityScene}
                            onIndexChange={setActivityTab}
                            renderTabBar={renderTabBar}
                            swipeEnabled={isNative}
                            lazy
                        />
                    </View>
                </View>
            </View>
        );
    }

    // ============ MOBILE ============
    return (
        <View style={styles.container}>
            <TabView
                navigationState={{ index: mobileTab, routes: mobileRoutes }}
                renderScene={renderMobileScene}
                onIndexChange={setMobileTab}
                renderTabBar={renderTabBar}
                swipeEnabled={isNative}
                lazy
            />
        </View>
    );
};

const styles = StyleSheet.create((theme, rt) => ({
    container: { flex: 1, backgroundColor: 'transparent' },

    // Loading State
    loadingContainer: { flex: 1, padding: 20, maxWidth: 500, width: rt.isLandscape ? '50%' : '100%' },
    loadingHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 20 },
    loadingHeaderInfo: { flex: 1, marginLeft: 16, justifyContent: 'center' },
    loadingStatsRow: { flexDirection: 'row', gap: 10, marginBottom: 20 },
    loadingStatItem: { flex: 1 },

    // Two Column Layout
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, maxWidth: 420 },
    leftPanelContent: { padding: 16, paddingBottom: 100 },
    rightPanel: { flex: 2 },

    // Header Card
    headerCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 16, padding: 20, marginBottom: 16,
        shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 3,
    },

    // Locked Badge + Lock Toggle row
    lockedRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    lockedBadge: {
        flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
        backgroundColor: theme.colors.warningContainer, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6, marginBottom: 12,
    },
    lockedBadgeText: { color: theme.colors.warning, fontSize: 12, fontFamily: theme.fonts.bold },

    // Lock Toggle (Account Owner only)
    lockToggleButton: (locked) => ({
        padding: 8, borderRadius: 8, marginLeft: 'auto', marginBottom: 12,
        _web: {
            cursor: 'pointer', transition: 'all 0.15s ease',
            opacity: locked ? 1 : 0.6,
            _hover: { opacity: 1, backgroundColor: theme.colors.warningContainer },
        },
    }),

    // Title Row
    titleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
    iconBadge: {
        width: 52, height: 52, backgroundColor: theme.colors.secondaryContainer,
        borderRadius: 10, alignItems: 'center', justifyContent: 'center',
    },
    titleInfo: { flex: 1, marginLeft: 14 },
    permissionTitle: { fontSize: 18, fontFamily: theme.fonts.heading, color: theme.colors.text },
    permissionSubtitle: { fontSize: 13, color: theme.colors.textSecondary, marginTop: 2 },

    // Stats Row
    statsRow: {
        flexDirection: 'row', alignItems: 'center', paddingVertical: 14,
        borderTopWidth: 1, borderColor: theme.colors.border,
    },
    statItem: { flex: 1, alignItems: 'center' },
    statDivider: { width: 1, height: 28, backgroundColor: theme.colors.border },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 2 },
    statValue: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Info Section
    infoSection: {
        backgroundColor: theme.colors.surface, borderRadius: 16, padding: 16, marginBottom: 16,
        shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2,
    },
    sectionLabel: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 12 },

    // Role Preview Items (in InfoPanel)
    rolePreviewItem: {
        flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10,
        borderBottomWidth: 1, borderBottomColor: theme.colors.border,
    },
    rolePreviewIcon: {
        width: 32, height: 32, borderRadius: 8, backgroundColor: theme.colors.primaryContainer,
        alignItems: 'center', justifyContent: 'center',
    },
    rolePreviewInfo: { flex: 1 },
    rolePreviewName: { fontSize: 14, fontFamily: theme.fonts.heading, color: theme.colors.text },
    rolePreviewDesc: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 1 },
    moreText: { fontSize: 12, color: theme.colors.textSecondary, fontStyle: 'italic', marginTop: 8, textAlign: 'center' },

    // Tab Content
    tabContent: { flex: 1, padding: rt.isLandscape ? 10 : 0 },
    listContent: { padding: 16, paddingBottom: 100 },

    // Role Item (in Roles Tab)
    roleItem: {
        flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14,
        backgroundColor: theme.colors.surface, borderRadius: 12, marginBottom: 8,
        borderWidth: 1, borderColor: theme.colors.border,
        _web: { transition: 'all 0.15s ease-in-out', _hover: { backgroundColor: theme.colors.hover } },
    },
    roleItemIcon: {
        width: 36, height: 36, borderRadius: 8, backgroundColor: theme.colors.primaryContainer,
        alignItems: 'center', justifyContent: 'center',
    },
    roleItemInfo: { flex: 1 },
    roleItemName: { fontSize: 14, fontFamily: theme.fonts.heading, color: theme.colors.text },
    roleItemDesc: { fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 },

    // User Card
    userCard: {
        backgroundColor: theme.colors.surface, borderRadius: 12, padding: 14, marginBottom: 10,
        borderWidth: 1, borderColor: theme.colors.border,
        _web: { transition: 'all 0.15s ease-in-out', _hover: { backgroundColor: theme.colors.hover } },
    },
    userCardHeader: { flexDirection: "row", alignItems: "center", gap: 12 },
    userCardInfo: { flex: 1 },
    userNameRow: { flexDirection: "row", alignItems: "center", gap: 6 },
    userName: { fontSize: 15, fontFamily: theme.fonts.heading, color: theme.colors.text },
    userStatus: { fontSize: 13, color: theme.colors.textSecondary, marginTop: 2 },
    verifiedIcon: { color: theme.colors.info },
    adminIcon: { color: theme.colors.warning },

    // Roles Section (in User Card)
    rolesSection: { marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: theme.colors.border },
    rolesSectionLabel: { fontSize: 12, color: theme.colors.textSecondary, marginBottom: 8 },
    rolesRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    rolePill: {
        flexDirection: "row", alignItems: "center", gap: 4,
        paddingVertical: 6, paddingHorizontal: 10, backgroundColor: theme.colors.primaryContainer, borderRadius: 20,
    },
    rolePillText: { fontFamily: theme.fonts.body, color: theme.colors.primary, fontSize: 12 },
}));

export default PermissionScreen;